import React, { useCallback, useState } from "react";
import { StatusEnum } from "../../hoc/with-status";
import { getJobScheduleDetails } from "../../utility/application-data";
import { useParams } from "react-router-dom";
import { addEventMetricWithBBId } from "../../utility/adobe-analytics";
import { ZapposPurchasePromotion } from "./zappos-purchase-promotion";
import { useNavigatorWithURLReadyOnOpeningNewTab } from "../../hooks/use-navigator";
import moment from "moment";

export interface ZapposPurchaseProps {
  status: StatusEnum;
  href: string;
  taskId: string;
}

export const ZapposPurchaseTask = ({
  status,
  href,
  taskId,
}: ZapposPurchaseProps) => {
  const { applicationId } = useParams<{
    applicationId: string;
  }>();

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      await addEventMetricWithBBId(window, applicationId, eventName);
    },
    [applicationId]
  );

  const trackZapposPurchaseClick = useCallback(
    () => sendAnalyticsEvent("Make Shoe Purchase"),
    [sendAnalyticsEvent]
  );

  const navigateToUrl = useNavigatorWithURLReadyOnOpeningNewTab(href);

  const navigateToZappos = useCallback(() => {
    sendAnalyticsEvent("Make Shoe Purchase");
    copyTextToClipboard(taskId);
    navigateToUrl();
  }, [trackZapposPurchaseClick, navigateToUrl, taskId]);

  const [passedFirstDayOnsite, setPassedFirstDayOnsite] = useState<boolean>(
    false
  );

  const decidedIfFirstDayOnsitePassed = useCallback(async () => {
    if (process.env.NODE_ENV === "development") {
      if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
        console.warn("Skip first day onsite check");
        setPassedFirstDayOnsite(false);
        return;
      }
    }
    const scheduleDetails = await getJobScheduleDetails(applicationId);
    const firstDayOnsite = scheduleDetails.firstDayOnSite;
    const currentDate = moment.utc().local();
    setPassedFirstDayOnsite(currentDate.isSameOrAfter(firstDayOnsite));
  }, [applicationId]);

  return (
    <ZapposPurchasePromotion
      status={status}
      decidedIfFirstDayOnsitePassed={decidedIfFirstDayOnsitePassed}
      navigateToZappos={navigateToZappos}
      hasPassedFirstDayOnsite={passedFirstDayOnsite}
      taskId={taskId}
    />
  );
};

async function copyTextToClipboard(text: string) {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  await navigator.clipboard.writeText(text).then(
    function () {},
    function (err) {
      console.error("Unable to async copy to clipboard: ", err);
    }
  );
}

function fallbackCopyTextToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    console.error("Unable to fallback copy to clipboard: ", err);
  }

  document.body.removeChild(textArea);
}
